/**
 * WE-446 — Trial blur (paywall masking on screens).
 *
 * Two pieces:
 *  - `useTrialBlur()` — single source of truth: should the current user see
 *    the legacy paywall masking (History banner, masked product rows, locked
 *    advanced table)? Returns `{ isBlurred, isLoading }`. Consumers should
 *    treat `isLoading` as "assume blurred" so real data doesn't briefly leak
 *    to a trial-blurred user before the storage/subscription queries resolve.
 *  - `useTrialBlurUrlParam()` — one-shot URL-param capture. On a fresh signup
 *    a `?trial_blur=1` link sticks the override into user storage. Once set
 *    it cannot be flipped off from the URL.
 *
 * Pre-auth visitors (the param lands on `/login` before the session exists)
 * are handled in the login page: it stashes the value into sessionStorage
 * under `TRIAL_BLUR_PENDING_KEY`, and this hook drains it after auth.
 */
import { useEffect, useRef } from 'react';
import {
    useGetCurrentSubscriptionQuery,
} from '@/store/slices/subscriptionSlice';
import {
    useGetUserStorageQuery,
    useSaveUserStorageMutation,
} from '@/store/slices/userSlice';

export const TRIAL_BLUR_URL_PARAM = 'trial_blur';
export const TRIAL_BLUR_PENDING_KEY = 'we446.pendingTrialBlur';

const drainPending = (): void => {
    if (typeof window === 'undefined') return;
    try {
        window.sessionStorage.removeItem(TRIAL_BLUR_PENDING_KEY);
    } catch {
        // sessionStorage may be unavailable (privacy mode); ignore.
    }
};

const readPending = (): boolean => {
    if (typeof window === 'undefined') return false;
    try {
        return window.sessionStorage.getItem(TRIAL_BLUR_PENDING_KEY) === '1';
    } catch {
        return false;
    }
};

/**
 * Stash `?trial_blur=1` into sessionStorage when the user lands pre-auth.
 * Safe to call multiple times; only writes when the param is present.
 * Call this from public routes (e.g. /login) where the user is not yet logged in.
 */
export const stashTrialBlurFromUrl = (search: URLSearchParams | null | undefined): void => {
    if (typeof window === 'undefined' || !search) return;
    if (search.get(TRIAL_BLUR_URL_PARAM) === '1') {
        try {
            window.sessionStorage.setItem(TRIAL_BLUR_PENDING_KEY, '1');
        } catch {
            // sessionStorage may be unavailable (privacy mode); ignore.
        }
    }
};

export interface TrialBlurState {
    /** True when the current user should see the legacy paywall masking. */
    isBlurred: boolean;
    /** True while storage/subscription queries are still resolving. */
    isLoading: boolean;
}

/**
 * Returns the trial-blur state. Consumers should treat `isLoading` as
 * "assume blurred" (safe default): during load, hide real data behind the
 * paywall until the state is confirmed, so nothing leaks to a trial-blurred
 * user in the ~50-500ms load window.
 */
export const useTrialBlur = (): TrialBlurState => {
    const { data: storage, isLoading: storageLoading } = useGetUserStorageQuery();
    const { data: currentSubscription, isLoading: subscriptionLoading } = useGetCurrentSubscriptionQuery();
    const forceTrialBlur = storage?.data?.forceTrialBlur === true;
    const hasActiveSubscription = currentSubscription?.status === 'active';
    return {
        isBlurred: forceTrialBlur && !hasActiveSubscription,
        isLoading: storageLoading || subscriptionLoading,
    };
};

/**
 * One-shot side effect: persists the trial-blur override to user storage
 * if it came from a URL param (or was stashed pre-auth). Mount once inside
 * a private layout, after `useGetUserStorageQuery` is also being polled
 * (so we know whether the field is already set).
 *
 * Uses `window.location.search` (not next/navigation's useSearchParams) to
 * avoid opting the entire (private) layout into dynamic rendering. The URL
 * param is only read at signup, so we don't need reactivity to param changes.
 */
export const useTrialBlurUrlParam = (): void => {
    const { data: storage, isLoading } = useGetUserStorageQuery();
    const [saveUserStorage] = useSaveUserStorageMutation();
    const didRunRef = useRef(false);

    useEffect(() => {
        if (didRunRef.current) return;
        if (isLoading || !storage) return;
        // Setting is sticky: once written, the URL param can never flip it again.
        // `data` may be absent entirely on a fresh account (empty storage).
        if (storage.data?.forceTrialBlur !== undefined) {
            didRunRef.current = true;
            drainPending();
            return;
        }

        const urlSearch = typeof window !== 'undefined' ? window.location.search : '';
        const urlSays = new URLSearchParams(urlSearch).get(TRIAL_BLUR_URL_PARAM) === '1';
        const pendingSays = readPending();
        if (!urlSays && !pendingSays) return;

        didRunRef.current = true;
        saveUserStorage({ forceTrialBlur: true })
            .unwrap()
            .then(drainPending)
            .catch(() => {
                // Persist failure: let a subsequent mount (or refetch) retry.
                // Keep the pending sessionStorage flag intact so the URL param
                // isn't required a second time.
                didRunRef.current = false;
            });
    }, [isLoading, storage, saveUserStorage]);
};
